import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, LogOut, Download } from "lucide-react";
import Seo from "../components/common/Seo";
import { adminLogin, fetchLeads } from "../lib/api";

const KEY = "kv_admin_token";

function jwtExp(t) {
  try {
    const p = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof p.exp === "number" ? p.exp : null;
  } catch {
    return null;
  }
}
const inputCls =
  "w-full bg-kv-bg2 border border-kv-steel/30 px-4 py-3 font-body text-sm text-kv-off placeholder:text-kv-steel/70 focus:outline-none focus:border-kv-electric focus:ring-1 focus:ring-kv-electric";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY) || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  const [query, setQuery] = useState("");
  const [serviceFilter, setServiceFilter] = useState(null);
  const [emailNotSent, setEmailNotSent] = useState(false);

  const serviceChips = Array.from(new Set(leads.flatMap((l) => l.services_of_interest || []))).sort();
  const filtersActive = Boolean(query.trim()) || Boolean(serviceFilter) || emailNotSent;

  const filteredLeads = leads.filter((l) => {
    const q = query.trim().toLowerCase();
    const matchesQuery =
      !q ||
      [l.full_name, l.company_name, l.work_email, (l.services_of_interest || []).join(" "), l.industry]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    const matchesService = !serviceFilter || (l.services_of_interest || []).includes(serviceFilter);
    const matchesEmail = !emailNotSent || !l.email_delivered;
    return matchesQuery && matchesService && matchesEmail;
  });

  const signOut = useCallback((wasExpired = false) => {
    localStorage.removeItem(KEY);
    setToken("");
    setLeads([]);
    setExpired(wasExpired);
  }, []);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const data = await fetchLeads(t);
      setLeads(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        signOut(true);
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  useEffect(() => { if (token) load(token); }, [token, load]);

  // Proactively sign out when the JWT lapses (8h), with a gentle prompt.
  useEffect(() => {
    if (!token) return;
    const exp = jwtExp(token);
    if (!exp) return;
    const ms = exp * 1000 - Date.now();
    if (ms <= 0) { signOut(true); toast.error("Session expired. Please sign in again."); return; }
    const id = setTimeout(() => { signOut(true); toast.error("Session expired. Please sign in again."); }, ms);
    return () => clearTimeout(id);
  }, [token, signOut]);

  const onLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const res = await adminLogin(password);
      localStorage.setItem(KEY, res.token);
      setExpired(false);
      setPassword("");
      setToken(res.token);
      toast.success("Signed in.");
    } catch {
      toast.error("Invalid password.");
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => signOut(false);

  const exportCsv = () => {
    const cols = [
      ["full_name", "Name"], ["company_name", "Company"], ["work_email", "Email"], ["phone", "Phone"],
      ["industry", "Industry"], ["company_size", "Company size"], ["services_of_interest", "Services"],
      ["primary_objective", "Objective"], ["current_stack", "Current stack"], ["preferred_contact", "Preferred contact"],
      ["message", "Message"], ["email_delivered", "Email sent"], ["created_at", "Date"],
    ];
    const esc = (val) => {
      let v = val;
      if (v == null) v = "";
      if (Array.isArray(v)) v = v.join("; ");
      if (typeof v === "boolean") v = v ? "Yes" : "No";
      v = String(v);
      return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
    };
    const rows = [cols.map((c) => esc(c[1])).join(",")].concat(
      filteredLeads.map((l) => cols.map((c) => esc(l[c[0]])).join(","))
    );
    const blob = new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kratos-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredLeads.length} lead${filteredLeads.length === 1 ? "" : "s"}.`);
  };

  if (!token) {
    return (
      <>
        <Seo title="Admin — Kratos Venture Group" description="Restricted." />
        <section className="relative min-h-screen flex items-center kv-radial" data-testid="admin-login">
          <div className="absolute inset-0 kv-grid-bg opacity-25" aria-hidden />
          <div className="relative max-w-sm w-full mx-auto px-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-kv-royal mb-4">Restricted</div>
            <h1 className="font-display text-3xl font-medium tracking-tight text-kv-off">Lead console</h1>
            {expired && (
              <p className="mt-3 font-body text-sm text-kv-grey" data-testid="admin-session-expired">
                Your session expired — please sign in again.
              </p>
            )}
            <form onSubmit={onLogin} className="mt-8 space-y-4">
              <input type="password" className={inputCls} placeholder="Admin password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
              <button type="submit" disabled={authLoading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-kv-off text-kv-bg border border-kv-off hover:bg-kv-navy transition-colors disabled:opacity-60" data-testid="admin-login-btn">
                {authLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </button>
            </form>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo title="Lead console — Kratos Venture Group" description="Restricted." />
      <section className="relative min-h-screen pt-28 pb-20" data-testid="admin-dashboard">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-kv-royal">Lead console</div>
              <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-kv-off">Strategy call requests</h1>
              <p className="mt-2 font-body text-sm text-kv-steel">{filtersActive ? `${filteredLeads.length} of ${leads.length}` : `${leads.length} total`}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, company, service…"
                data-testid="admin-search"
                className="w-56 px-3 py-2.5 bg-kv-bg border border-kv-steel/30 text-sm text-kv-off placeholder:text-kv-steel focus:outline-none focus:border-kv-electric/50 transition-colors"
              />
              <button onClick={exportCsv} disabled={filteredLeads.length === 0} className="inline-flex items-center gap-2 px-4 py-2.5 border border-kv-steel/30 text-kv-off hover:border-kv-electric/50 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-kv-steel/30" data-testid="admin-export-csv">
                <Download className="h-4 w-4" /> Export CSV
              </button>
              <button onClick={() => load(token)} className="inline-flex items-center gap-2 px-4 py-2.5 border border-kv-steel/30 text-kv-off hover:border-kv-electric/50 transition-colors text-sm" data-testid="admin-refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
              <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2.5 border border-kv-steel/30 text-kv-grey hover:text-kv-off transition-colors text-sm" data-testid="admin-logout">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </div>

          {leads.length > 0 && (
            <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="admin-filter-chips">
              <button
                onClick={() => setEmailNotSent((v) => !v)}
                data-testid="admin-chip-email-not-sent"
                aria-pressed={emailNotSent}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.14em] border transition-colors ${emailNotSent ? "bg-kv-off text-kv-bg border-kv-off" : "border-kv-steel/30 text-kv-grey hover:border-kv-electric/50"}`}
              >
                Email not sent
              </button>
              {serviceChips.map((svc) => {
                const active = serviceFilter === svc;
                return (
                  <button
                    key={svc}
                    onClick={() => setServiceFilter(active ? null : svc)}
                    data-testid={`admin-chip-service-${svc.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    aria-pressed={active}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.14em] border transition-colors ${active ? "bg-kv-off text-kv-bg border-kv-off" : "border-kv-steel/30 text-kv-grey hover:border-kv-electric/50"}`}
                  >
                    {svc}
                  </button>
                );
              })}
              {filtersActive && (
                <button
                  onClick={() => { setQuery(""); setServiceFilter(null); setEmailNotSent(false); }}
                  data-testid="admin-chip-clear"
                  className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.14em] text-kv-electric hover:text-kv-off transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-kv-steel" /></div>
          ) : leads.length === 0 ? (
            <div className="py-24 text-center font-body text-kv-steel">No leads yet.</div>
          ) : filteredLeads.length === 0 ? (
            <div className="py-24 text-center font-body text-kv-steel" data-testid="admin-no-matches">No leads match your filters.</div>
          ) : (
            <div className="overflow-x-auto border border-kv-steel/20">
              <table className="w-full text-left border-collapse" data-testid="admin-leads-table">
                <thead>
                  <tr className="bg-kv-bg2">
                    {["Name", "Company", "Email", "Phone", "Industry", "Services", "Email sent", "Date"].map((h) => (
                      <th key={h} className="font-mono text-[10px] uppercase tracking-wide text-kv-steel px-4 py-3 whitespace-nowrap border-b border-kv-steel/20">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((l) => (
                    <tr key={l.id} className="border-b border-kv-steel/10 hover:bg-kv-bg2/50" data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3 text-sm text-kv-off whitespace-nowrap">{l.full_name}</td>
                      <td className="px-4 py-3 text-sm text-kv-grey whitespace-nowrap">{l.company_name}</td>
                      <td className="px-4 py-3 text-sm text-kv-grey whitespace-nowrap">{l.work_email}</td>
                      <td className="px-4 py-3 text-sm text-kv-grey whitespace-nowrap">{l.phone}</td>
                      <td className="px-4 py-3 text-sm text-kv-grey whitespace-nowrap">{l.industry || "—"}</td>
                      <td className="px-4 py-3 text-sm text-kv-grey max-w-[220px] truncate">{(l.services_of_interest || []).join(", ") || "—"}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className={l.email_delivered ? "text-kv-electric" : "text-kv-steel"}>{l.email_delivered ? "Yes" : "No"}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-kv-steel whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
